/**
 * Bannières registre Performance vs Activité ops (contrat S1).
 * merchantFriendly = wording Analytics commerçant (sans M1/M2/M3).
 * Défaut = rendu Vue d’ensemble inchangé.
 */

export const M1_LABEL_FALLBACK = 'Activité payée';

export function PerformanceScopeBanner({
  resolver,
  periode,
  className = '',
  merchantFriendly = false,
}) {
  const m1Label = resolver?.m1Label || M1_LABEL_FALLBACK;
  const periodBit =
    periode?.debutLocal && periode?.finLocal
      ? `${periode.debutLocal} → ${periode.finLocal}`
      : '';

  if (merchantFriendly) {
    return (
      <div
        className={`rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900 ${className}`}
      >
        <p className="font-medium">Ce qui est pris en compte</p>
        <p className="mt-1 text-blue-800/90">
          {periodBit ? `${periodBit}. ` : ''}
          Les ventes payées de la période sont comptabilisées. Les remboursements sont déduits du
          chiffre d’affaires net. Sont inclus selon les modules actifs : Click &amp; Collect,
          livraison, ventes sur place et caisse.
          {resolver?.m1Subtext ? ` ${resolver.m1Subtext}` : ''}
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900 ${className}`}>
      <p className="font-medium">Performance commerciale (Analytics)</p>
      <p className="mt-1 text-blue-800/90">
        {periodBit ? `${periodBit} · ` : ''}
        Transactions <strong>payées</strong> (volume incl. remboursées) — CA net encaissé (brut − remboursements) — libellé M1 :{' '}
        <strong>{m1Label}</strong>.
        {resolver?.m1Subtext ? ` ${resolver.m1Subtext}` : ''}
      </p>
    </div>
  );
}

export function OpsRegistreBanner({ className = '' }) {
  return (
    <div className={`rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 ${className}`}>
      <p className="font-medium">Registre opérationnel — liste Commandes</p>
      <p className="mt-1 text-amber-900/90">
        Cette liste inclut commandes et RDV (hors caisse POS), tous statuts et sans filtre paiement.
        Elle sert au suivi ops et peut différer de la performance Analytics.
      </p>
    </div>
  );
}

export function PerformanceOpsGapBanner({ performanceVolume = 0, opsListCount = 0, className = '' }) {
  if (performanceVolume > 0 || opsListCount === 0) return null;
  return (
    <div className={`rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-950 ${className}`}>
      <p className="font-medium">Performance à 0, liste Commandes non vide</p>
      <p className="mt-1 text-orange-900/90">
        La performance Analytics compte uniquement l&apos;activité <strong>payée</strong> sur la période.
        La liste Commandes affiche l&apos;historique opérationnel ({opsListCount} ligne
        {opsListCount > 1 ? 's' : ''}) — ce n&apos;est pas un écart de bug.
      </p>
    </div>
  );
}

export function ActiviteByTypePanel({ activite, resolver, className = '' }) {
  const rows = (activite?.byType || []).filter((row) => Number(row.volume || 0) > 0);
  if (!rows.length) return null;
  if (resolver?.mode !== 'MIXTE' && !resolver?.isHybrid) return null;

  return (
    <div className={`rounded-lg border border-violet-100 bg-violet-50/60 px-4 py-3 ${className}`}>
      <p className="text-sm font-medium text-violet-900 mb-2">Répartition par type (activité payée)</p>
      <div className="flex flex-wrap gap-2">
        {rows.map((row) => (
          <span
            key={row.type}
            className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-white border border-violet-200 text-violet-900"
          >
            {resolver?.byTypeLabels?.[row.type] || row.type} : {row.volume}
            {row.ca > 0 ? ` · ${Number(row.ca).toFixed(2)} €` : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

export function PerformanceOfficialKpis({ performanceOverview, merchantFriendly = false }) {
  if (!performanceOverview?.kpi) return null;
  const kpi = performanceOverview.kpi;
  const resolver = performanceOverview.resolver || {};
  const fmt = (n) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));

  const caLabel = merchantFriendly ? 'CA net encaissé' : 'CA net encaissé (M2)';
  const volumeLabel =
    resolver.m1Label || kpi.volume?.label || M1_LABEL_FALLBACK;
  const panierLabel = merchantFriendly ? 'Panier moyen' : 'Panier moyen (M3)';

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-4">
        <p className="text-xs font-medium text-emerald-800 uppercase tracking-wide">{caLabel}</p>
        <p className="text-2xl font-bold text-emerald-900 mt-1">{fmt(kpi.ca?.value)}</p>
        {(kpi.ca?.brut > 0 || kpi.ca?.rembourse > 0) && (
          <p className="text-xs text-emerald-800/80 mt-1">
            {merchantFriendly ? 'Avant remboursements' : 'Brut'} {fmt(kpi.ca?.brut)}
            {kpi.ca?.rembourse > 0
              ? ` · ${merchantFriendly ? 'Remboursements' : 'Remboursé'} ${fmt(kpi.ca?.rembourse)}`
              : ''}
          </p>
        )}
      </div>
      <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
        <p className="text-xs font-medium text-blue-800 uppercase tracking-wide">{volumeLabel}</p>
        <p className="text-2xl font-bold text-blue-900 mt-1">{kpi.volume?.value ?? 0}</p>
      </div>
      <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 p-4">
        <p className="text-xs font-medium text-violet-800 uppercase tracking-wide">{panierLabel}</p>
        <p className="text-2xl font-bold text-violet-900 mt-1">{fmt(kpi.panierMoyen?.value)}</p>
      </div>
    </div>
  );
}
